import type { ConfiguredPortRef } from '@gat-ship/types'
import { shallowRef, ref } from 'vue'
import { getLocalPortRequest } from '../model/local-port-api-calls'

// Module-level singleton cache keyed by location code. Unlike the flat
// list datasources, configured ports are fetched on demand (one per
// selected port), so we memoise per code rather than fetching the whole
// set up front.
const cache = shallowRef<Record<string, ConfiguredPortRef>>({})
const loadingLocalPort = ref(false)

async function fetchLocalPort(
  locationCode: string,
  force?: true
): Promise<ConfiguredPortRef | undefined> {
  if (!locationCode) return undefined
  if (cache.value[locationCode] && !force) return cache.value[locationCode]
  loadingLocalPort.value = true
  try {
    const port = await getLocalPortRequest(locationCode)
    cache.value = { ...cache.value, [locationCode]: port }
    return port
  } finally {
    loadingLocalPort.value = false
  }
}

/** Synchronously read an already-fetched port, or `undefined`. */
function getCachedLocalPort(
  locationCode: string
): ConfiguredPortRef | undefined {
  return cache.value[locationCode]
}

export function useSelectLocalPortDataSource() {
  return {
    loadingLocalPort,
    fetchLocalPort,
    getCachedLocalPort
  }
}
